import { HTMLProps, PropsWithChildren, ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  DropdownMenuArrowProps,
  DropdownMenuContentProps,
  DropdownMenuItemProps,
  DropdownMenuLabelProps,
  DropdownMenuProps,
  DropdownMenuSeparatorProps,
  DropdownMenuTriggerProps,
} from "@radix-ui/react-dropdown-menu";
import styles from "./dropdown.module.scss";
import classNames from "classnames";

export interface DropdownProps extends DropdownMenuProps {
  trigger?: ReactNode;
  header?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  align?: DropdownMenuContentProps["align"];
  alignOffset?: DropdownMenuContentProps["alignOffset"];
  side?: DropdownMenuContentProps["side"];
  sideOffset?: DropdownMenuContentProps["sideOffset"];
  triggerAsChild?: DropdownMenuTriggerProps["asChild"];
  triggerClassName?: DropdownMenuTriggerProps["className"];
  contentContainerColor?: string;
  dropdownContentClass?: string | undefined;
  disabled?: boolean;
}

export const Dropdown = ({
  trigger,
  header,
  footer,
  children,
  modal = true,
  disabled = false,
  align,
  alignOffset,
  side,
  sideOffset,
  triggerAsChild = true,
  triggerClassName,
  contentContainerColor,
  dropdownContentClass,
  ...rest
}: DropdownProps) => {
  document.body.setAttribute("style", "position:static !important");

  const contentClasses = classNames(
    styles.container,
    styles.content,
    dropdownContentClass,
  );

  return (
    <DropdownMenu.Root modal={modal} {...rest}>
      {trigger && (
        <DropdownMenu.Trigger
          disabled={disabled}
          className={triggerClassName}
          asChild={triggerAsChild}
        >
          {trigger}
        </DropdownMenu.Trigger>
      )}
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          sideOffset={sideOffset}
          sticky={"always"}
          align={align}
          alignOffset={alignOffset}
          loop={true}
          className={contentClasses}
          style={{ backgroundColor: `${contentContainerColor}` }}
        >
          {header && header}

          {children}

          {footer && footer}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};

export type DropdownMenuHeaderVariant = "primary" | "secondary" | "centered";

export interface DropdownHeader {
  variant?: DropdownMenuHeaderVariant;
  children: ReactNode;
  headerClassName?: string | undefined;
}

const DropdownHeader = (props: DropdownHeader) => {
  const { variant = "primary", children, headerClassName } = props;

  const dropdownHeaderVariant = () => {
    switch (variant) {
      case `primary`:
        return styles.headerPrimary;
      case "secondary":
        return styles.headerSecondary;
      case "centered":
        return styles.headerCentered;
    }
  };

  const dropdownHeaderClasses = `${styles.header} ${dropdownHeaderVariant()}`;

  return (
    <div
      className={
        headerClassName != null || undefined
          ? headerClassName
          : dropdownHeaderClasses
      }
    >
      {children}
    </div>
  );
};

Dropdown.Header = DropdownHeader;

export type DropdownMenuItemVariant = "default" | "destructive";
export type DropdownMenuItemSize = "default" | "relaxed";

export interface DropdownItemProps extends DropdownMenuItemProps {
  children: ReactNode;
  variant?: DropdownMenuItemVariant;
  size?: DropdownMenuItemSize;
}

const DropdownItem = ({
  children,
  variant = "default",
  size = "default",
  className,
  ...rest
}: DropdownItemProps) => {
  const classes = classNames(className, styles.item, {
    [styles.disabled]: rest.disabled,
    [styles.destructive]: variant === "destructive",
    [styles.defaultSize]: size === "default",
    [styles.relaxedSize]: size === "relaxed",
  });

  return (
    <div className={styles.itemContainer}>
      <DropdownMenu.Item className={classes} {...rest}>
        {children}
      </DropdownMenu.Item>
    </div>
  );
};

Dropdown.Item = DropdownItem;

const DropdownArrow = (props: DropdownMenuArrowProps) => {
  return (
    <DropdownMenu.Arrow
      className={props.className ?? styles.arrow}
      {...props}
    />
  );
};

Dropdown.Arrow = DropdownArrow;

const DropdownSeparator = (props: DropdownMenuSeparatorProps) => {
  const classes = classNames(styles.separator);

  return <DropdownMenu.Separator className={classes} {...props} />;
};

Dropdown.Separator = DropdownSeparator;

const DropdownLabel = (props: DropdownMenuLabelProps) => {
  const classes = classNames(styles.label);

  return <DropdownMenu.Label className={classes} {...props} />;
};

Dropdown.Label = DropdownLabel;

const DropdownFooter = (
  props: PropsWithChildren<HTMLProps<HTMLDivElement>>,
) => {
  const dropdownFooterClasses = classNames(styles.footer, props.className);

  return <div className={dropdownFooterClasses}>{props.children}</div>;
};

Dropdown.Footer = DropdownFooter;
